import { useCallback, useMemo } from "react"

import {
  buildMetricDashboardSourcesById,
  useMetricsList,
} from "@/metrics/catalog"

import type { MetricFilter } from "../../types/core"
import { resolveChartMetric } from "../../utils/chart/chartWidgetState"
import {
  getWidgetMetricFilterError,
  getWidgetMetricFilters,
} from "../../utils/widgetMetricFilterValidation"
import {
  isWidgetMetricOutputType,
  withWidgetMetricOutputTypes,
} from "../../utils/widgetMetricOutputType"

export const useChartMetric = (
  metricId: string | undefined,
  metricFilter?: MetricFilter[],
  revalidateOnMount: boolean | "stale" = "stale",
) => {
  const referencedMetricIds = useMemo(
    () => [
      ...new Set([
        ...(metricId ? [metricId] : []),
        ...(getWidgetMetricFilters(metricFilter) ?? [])
          .map((filter) => filter.metricId)
          .filter(Boolean),
      ]),
    ],
    [metricId, metricFilter],
  )
  const {
    data: allMetricRecords,
    error,
    isLoading: isMetricLoading,
    metricIdAliases,
    metricDirectoryByManager: byManager,
    refetch,
  } = useMetricsList({ referencedMetricIds, revalidateOnMount })
  const recheckMetricCatalog = useCallback(() => {
    void refetch()
  }, [refetch])
  const dashboardMetrics = useMemo(
    () =>
      buildMetricDashboardSourcesById(
        withWidgetMetricOutputTypes(allMetricRecords, byManager),
        metricIdAliases,
      ),
    [allMetricRecords, metricIdAliases, byManager],
  )
  const metric = useMemo(
    () => resolveChartMetric(dashboardMetrics, metricId),
    [dashboardMetrics, metricId],
  )

  return {
    isMetricLoading,
    isMetricMissing:
      Boolean(metricId) &&
      !isMetricLoading &&
      !error &&
      allMetricRecords !== undefined &&
      !metric,
    metric,
    metricDefinitionError:
      metric && !isWidgetMetricOutputType(metric.output_type)
        ? "This widget's metric has an unsupported output type. Edit the widget to choose a supported metric."
        : null,
    metricFilterError: getWidgetMetricFilterError(
      metricFilter,
      dashboardMetrics,
    ),
    metricCatalogError: metric ? null : error,
    metricRefreshError: metric ? error : null,
    recheckMetricCatalog,
  }
}
